import { Link } from "react-router-dom";
import { Search, ShieldCheck, Phone } from "lucide-react";
import { brand, founder } from "@/data/site/brand";
import type { BlogPost } from "@/data/site/blog";

export const SidebarSearch = ({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) => (
  <div className="bg-card border border-border p-5 rounded-md">
    <h4 className="font-heading font-bold text-navy text-sm uppercase tracking-wider mb-3">
      Search
    </h4>
    <div className="relative">
      <Search aria-hidden="true" className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
      <label htmlFor="blog-search" className="sr-only">Search articles</label>
      <input
        id="blog-search"
        type="search"
        aria-label="Search articles"
        placeholder="Search articles..."
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full pl-10 pr-4 py-2.5 bg-background text-foreground placeholder:text-muted-foreground border border-border focus:outline-none focus:ring-2 focus:ring-gold text-sm rounded"
      />
    </div>
  </div>
);

export const SidebarCategories = ({
  categories,
  counts,
  active,
  onSelect,
  total,
}: {
  categories: string[];
  counts: Record<string, number>;
  active: string;
  onSelect: (c: string) => void;
  total: number;
}) => (
  <div className="bg-card border border-border p-5 rounded-md">
    <h4 className="font-heading font-bold text-navy text-sm uppercase tracking-wider mb-3">
      Browse Categories
    </h4>
    <ul className="space-y-1.5">
      <li>
        <button
          onClick={() => onSelect("All")}
          className={`w-full flex justify-between items-center text-sm px-2 py-1.5 transition-colors rounded ${
            active === "All"
              ? "text-gold font-semibold bg-gold/5"
              : "text-muted-foreground hover:text-navy"
          }`}
        >
          <span>All</span>
          <span className="text-xs">{total}</span>
        </button>
      </li>
      {categories.map((cat) => (
        <li key={cat}>
          <button
            onClick={() => onSelect(cat)}
            className={`w-full flex justify-between items-center text-sm px-2 py-1.5 transition-colors rounded ${
              active === cat
                ? "text-gold font-semibold bg-gold/5"
                : "text-muted-foreground hover:text-navy"
            }`}
          >
            <span>{cat}</span>
            <span className="text-xs">{counts[cat] || 0}</span>
          </button>
        </li>
      ))}
    </ul>
  </div>
);

export const SidebarPopular = ({ posts }: { posts: BlogPost[] }) => (
  <div className="bg-card border border-border p-5 rounded-md">
    <h4 className="font-heading font-bold text-navy text-sm uppercase tracking-wider mb-3">
      Popular Articles
    </h4>
    <ul className="space-y-3">
      {posts.slice(0, 5).map((p, i) => (
        <li key={p.slug} className="flex gap-2 items-start">
          <span className="text-gold font-heading font-bold text-sm leading-none mt-0.5">
            {String(i + 1).padStart(2, "0")}
          </span>
          <Link
            to={`/blog/${p.slug}`}
            className="text-sm text-muted-foreground hover:text-gold transition-colors line-clamp-2 leading-snug"
          >
            {p.title}
          </Link>
        </li>
      ))}
    </ul>
  </div>
);

export const SidebarEditorialStandards = () => (
  <div className="bg-card border border-border p-5 rounded-md">
    <div className="flex items-center gap-2 mb-2">
      <ShieldCheck className="w-4 h-4 text-emerald-600" />
      <h4 className="font-heading font-bold text-navy text-sm uppercase tracking-wider">
        Editorial Standards
      </h4>
    </div>
    <p className="text-xs text-muted-foreground leading-relaxed mb-3">
      Every article on this site is written or reviewed by{" "}
      <Link to="/about/franklin-sofi" className="font-semibold text-navy hover:text-gold">
        {founder.name}, {founder.credentials}
      </Link>{" "}
      — founder of {brand.legalName}. We cite primary IRS sources. Tax law changes
      frequently; confirm current rules with a CPA before acting.
    </p>
    <Link
      to="/about/franklin-sofi"
      className="text-xs font-semibold text-navy hover:text-gold inline-flex items-center gap-1"
    >
      Meet our reviewer →
    </Link>
  </div>
);

export const SidebarCallCTA = () => (
  <div className="bg-gradient-navy p-6 rounded-md">
    <Phone className="w-6 h-6 text-gold mb-3" />
    <h4 className="font-heading font-bold text-white text-base mb-1">
      Talk to a Tax CPA
    </h4>
    <p className="text-xs text-white/70 mb-4 leading-relaxed">
      Free, confidential 15-minute case review with a Certified Tax Resolution Specialist.
    </p>
    <a
      href={brand.phoneTel ? `tel:${brand.phoneTel}` : `tel:${brand.phone}`}
      className="block text-center bg-gold text-navy-dark hover:bg-gold-light transition-colors font-bold text-sm py-2.5 rounded"
    >
      {brand.phone}
    </a>
  </div>
);
